import { usePageSeo } from '../hooks/usePageSeo';
import type { SeoEntry } from '../data/seo';
import { t, locale } from '../i18n';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, ExternalLink, Github, Layers, Tag } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { PortfolioItem } from '../context/PortfolioContext';
import { useLocalizedPortfolio as usePortfolio } from '../hooks/useLocalizedContent';
import { getImageUrl } from '../utils/imageUtils';

const PortfolioProject = () => {
  const { slug } = useParams<{ slug: string }>();
  const { getProjectBySlug, projects, loading } = usePortfolio();
  const navigate = useNavigate();
  const [project, setProject] = useState<PortfolioItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProject = async () => {
      if (!slug) return;

      try {
        setIsLoading(true);
        const fetchedProject = await getProjectBySlug(slug);
        setProject(fetchedProject || null);

        if (!fetchedProject && projects.length > 0) {
          navigate('/portfolio', { replace: true });
        }
      } catch (err) {
        console.error('Error fetching project:', err);
        setError(t("Failed to load project details"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchProject();
    window.scrollTo(0, 0);
  }, [slug, getProjectBySlug, navigate, projects.length]);

  const pageSeo = useMemo<SeoEntry>(() => ({
    title: project ? `${project.title} | Virtuoz Solutions Portfolio` : t("Project not found"),
    description: project?.description || '',
    canonicalPath: `/portfolio/${slug || ''}`,
    type: 'SoftwareApplication',
    image: project?.image,
  }), [project, slug]);
  usePageSeo(pageSeo);

  const relatedProjects = useMemo(() => {
    if (!project) return [];

    return projects
      .filter((candidate) => candidate.id !== project.id && candidate.category === project.category)
      .slice(0, 3);
  }, [project, projects]);

  if (isLoading || loading) {
    return (
      <div className="max-w-4xl py-12 text-center">
        <div className="animate-pulse">
          <div className="h-8 bg-card w-1/3 mx-auto mb-4 rounded"></div>
          <div className="h-4 bg-card w-1/4 mx-auto mb-8 rounded"></div>
          <div className="h-64 bg-card w-full mx-auto rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="max-w-4xl py-12 text-center">
        <h2 className="text-2xl font-bold mb-4">{t("Unable to load project")}</h2>
        <p className="text-foreground/70 mb-8">{error || t("Project not found")}</p>
        <Link to="/portfolio" className="btn btn-primary">
          {t("Return to Portfolio")}
        </Link>
      </div>
    );
  }

  const {
    title,
    longDescription,
    image,
    technologies,
    category,
    url,
    github,
    gallery = [],
    date,
  } = project;

  const formattedDate = date
    ? new Date(date).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  const galleryImages = gallery.filter(Boolean);

  return (
    <div className="w-full">
      {/* Header */}
      <section className="max-w-5xl py-8 md:py-12">
        <Link to="/portfolio" className="inline-flex items-center text-foreground/70 hover:text-foreground mb-6 group transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
          {t("Back to all projects")}
        </Link>

        <div className="flex flex-wrap gap-2 mb-4">
          <span className="bg-primary/10 text-primary text-sm px-3 py-1 rounded">
            {category}
          </span>
          {technologies.slice(0, 3).map((technology) => (
            <span
              key={technology}
              className="bg-card border border-foreground/10 text-foreground/70 text-sm px-3 py-1 rounded"
            >
              {technology}
            </span>
          ))}
        </div>

        <h1 className="font-display text-fluid-3xl mb-6">{title}</h1>

        <div className="flex flex-wrap gap-6 text-foreground/60 text-sm">
          {formattedDate && (
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              {formattedDate}
            </div>
          )}
          <div className="flex items-center">
            <Layers className="w-4 h-4 mr-2" />
            {category}
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {image && (
        <section className="py-4">
          <div className="aspect-[16/8] overflow-hidden rounded-lg bg-foreground/5">
            <img
              src={getImageUrl(image)}
              alt={title}
              className="w-full h-full object-cover"
            />
          </div>
        </section>
      )}

      {/* Content */}
      <section className="py-8">
        <div className="grid grid-cols-1 gap-10 xl:grid-cols-[minmax(0,860px)_340px] xl:items-start 2xl:grid-cols-[minmax(0,920px)_380px] 2xl:gap-14">
          <div className="min-w-0">
            <article className="blog-content" lang={project.contentLanguage || project.sourceLanguage}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkBreaks]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  h1: ({ children }) => <h2>{children}</h2>,
                }}
              >
                {longDescription || ''}
              </ReactMarkdown>
            </article>

            {galleryImages.length > 0 && (
              <div className="mt-12 pt-6 border-t border-foreground/10">
                <h2 className="font-heading text-2xl font-bold mb-6">{t("Gallery")}</h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {galleryImages.map((galleryImage, index) => (
                    <div key={`${galleryImage}-${index}`} className="aspect-video overflow-hidden rounded-lg bg-foreground/5">
                      <img
                        src={getImageUrl(galleryImage)}
                        alt={`${title} — ${t("Gallery")} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside>
            <div className="space-y-6 xl:sticky xl:top-8">
              <div className="rounded-lg border border-foreground/10 bg-card p-6">
                <h3 className="font-bold mb-4">{t("Project Links")}</h3>
                <div className="space-y-3">
                  {url && (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary w-full !justify-start !py-2 !px-4"
                    >
                      <ExternalLink className="w-4 h-4" />
                      {t("Visit Project")}
                    </a>
                  )}

                  {github && (
                    <a
                      href={github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary w-full !justify-start !py-2 !px-4"
                    >
                      <Github className="w-4 h-4" />
                      {t("View Code")}
                    </a>
                  )}

                  {!url && !github && (
                    <p className="text-foreground/60 text-sm">
                      {t("No public links are available for this project.")}
                    </p>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-foreground/10 bg-card p-6">
                <h3 className="font-bold mb-4">Technologies</h3>
                <div className="flex flex-wrap gap-2">
                  {technologies.map((technology) => (
                    <span
                      key={technology}
                      className="bg-primary/10 text-primary px-3 py-1 rounded-lg text-sm"
                    >
                      {technology}
                    </span>
                  ))}
                </div>
              </div>

              {relatedProjects.length > 0 && (
                <div className="rounded-lg border border-foreground/10 bg-card p-6">
                  <h3 className="font-bold mb-4">{t("Related Projects")}</h3>
                  <div className="space-y-4">
                    {relatedProjects.map((relatedProject) => (
                      <Link
                        key={relatedProject.id}
                        to={`/portfolio/${relatedProject.slug}`}
                        className="block group"
                      >
                        <div className="flex gap-4">
                          <div className="w-16 h-12 overflow-hidden rounded-lg flex-shrink-0 bg-foreground/5">
                            <img
                              src={getImageUrl(relatedProject.image)}
                              alt={relatedProject.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            />
                          </div>
                          <div>
                            <h4 className="text-sm font-medium group-hover:text-primary transition-colors line-clamp-2">
                              {relatedProject.title}
                            </h4>
                            <p className="flex items-center gap-1 text-foreground/50 text-xs mt-1">
                              <Tag className="w-3 h-3" />
                              {relatedProject.category}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default PortfolioProject;
